const PIPE_CHAR = '|'
const SEPARATOR_CELL_PATTERN = /^-{3,}$/
const BARE_SEPARATOR_PATTERN = /^[-: |]+$/
const MIN_PIPES_FOR_TABLE_LINE = 2

const countPipes = (line: string): number => {
  let count = 0
  for (const char of line) {
    if (char === PIPE_CHAR) {
      count++
    }
  }
  return count
}

const isTableLine = (line: string): boolean => countPipes(line) >= MIN_PIPES_FOR_TABLE_LINE

const isBareSeparator = (line: string): boolean => {
  const trimmed = line.trim()
  return trimmed.length >= 3 && trimmed.includes('-') && BARE_SEPARATOR_PATTERN.test(trimmed)
}

const splitCells = (line: string): string[] => {
  let inner = line.trim()
  if (inner.startsWith(PIPE_CHAR)) {
    inner = inner.slice(1)
  }
  if (inner.endsWith(PIPE_CHAR)) {
    inner = inner.slice(0, -1)
  }

  return inner.split(PIPE_CHAR).map((cell) => cell.trim())
}

const isSeparatorRow = (line: string): boolean => {
  const cellList = splitCells(line)
  if (cellList.length === 0) {
    return false
  }
  return cellList.every((cell) => SEPARATOR_CELL_PATTERN.test(cell.replace(/:/g, '')))
}

const buildSeparatorRow = (columnCount: number): string => {
  const cellList = Array.from({ length: columnCount }, () => '---')
  return `| ${cellList.join(' | ')} |`
}

const padRow = (line: string, targetColumnCount: number): string => {
  const cellList = splitCells(line)
  if (cellList.length === targetColumnCount) {
    return `| ${cellList.join(' | ')} |`
  }

  if (cellList.length > targetColumnCount) {
    const trimmedCellList = cellList.slice(0, targetColumnCount)
    return `| ${trimmedCellList.join(' | ')} |`
  }

  const paddedCellList = [
    ...cellList,
    ...Array.from({ length: targetColumnCount - cellList.length }, () => ''),
  ]
  return `| ${paddedCellList.join(' | ')} |`
}

interface TableBlock {
  startIndex: number
  endIndex: number
  lineList: string[]
}

const isTableOrSeparator = (line: string): boolean => isTableLine(line) || isBareSeparator(line)

const findTableBlocks = (lineList: string[]): TableBlock[] => {
  const blockList: TableBlock[] = []
  let i = 0

  while (i < lineList.length) {
    if (!isTableLine(lineList[i])) {
      i++
      continue
    }

    const startIndex = i
    i++

    while (i < lineList.length && isTableOrSeparator(lineList[i])) {
      i++
    }

    const tableLineCount = i - startIndex
    if (tableLineCount >= 2) {
      blockList.push({
        startIndex,
        endIndex: i - 1,
        lineList: lineList.slice(startIndex, i),
      })
    }
  }

  return blockList
}

const repairTableBlock = (block: TableBlock): string[] => {
  const { lineList } = block

  const headerCellList = splitCells(lineList[0])
  const columnCount = headerCellList.length

  const hasSeparator = lineList.length > 1 && isSeparatorRow(lineList[1])

  const repairedLineList: string[] = [padRow(lineList[0], columnCount)]

  if (!hasSeparator) {
    repairedLineList.push(buildSeparatorRow(columnCount))
  }

  const dataStartIndex = hasSeparator ? 2 : 1

  if (hasSeparator) {
    repairedLineList.push(buildSeparatorRow(columnCount))
  }

  for (let i = dataStartIndex; i < lineList.length; i++) {
    if (isSeparatorRow(lineList[i])) {
      continue
    }
    repairedLineList.push(padRow(lineList[i], columnCount))
  }

  return repairedLineList
}

export const repairMarkdownTables = (content: string): string => {
  const lineList = content.split('\n')
  const blockList = findTableBlocks(lineList)

  if (blockList.length === 0) {
    return content
  }

  const resultLineList: string[] = []
  let lastProcessedIndex = -1

  for (const block of blockList) {
    for (let i = lastProcessedIndex + 1; i < block.startIndex; i++) {
      resultLineList.push(lineList[i])
    }

    const needsBlankBefore =
      block.startIndex > 0 &&
      resultLineList.length > 0 &&
      resultLineList[resultLineList.length - 1].trim() !== ''
    if (needsBlankBefore) {
      resultLineList.push('')
    }

    const repairedLineList = repairTableBlock(block)
    resultLineList.push(...repairedLineList)

    const needsBlankAfter =
      block.endIndex < lineList.length - 1 && lineList[block.endIndex + 1].trim() !== ''
    if (needsBlankAfter) {
      resultLineList.push('')
    }

    lastProcessedIndex = block.endIndex
  }

  for (let i = lastProcessedIndex + 1; i < lineList.length; i++) {
    resultLineList.push(lineList[i])
  }

  return resultLineList.join('\n')
}
